import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Observable, Subject, Subscription, of, timer } from "rxjs";
import { catchError, distinctUntilChanged, map, switchMap, takeUntil } from "rxjs/operators";
import { DetalleDte } from "src/app/models/Dte/DetalleDte";
import { DteEmitido } from "src/app/models/Dte/DteEmitido";
import { DteFilter } from "src/app/models/Dte/DteFilter";
import { IDte } from "src/app/models/Dte/IDte";
import { Emisor } from "src/app/models/Emisor/Emisor";
import { Sucursal } from "src/app/models/Sucursal";
import { TipoDte } from "src/app/models/tipoDte";
import { DteEmitidosService } from "src/app/services/dte-emitidos.service";
import { FacturacionIndividualService } from "src/app/services/facturacion-individual.service";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { TokenStorageService } from "src/app/services/token-storage.service";
import { UtilitiesService } from "src/app/services/utilities.service";
import { Page } from "src/app/shared/models/Page";
import { PagedResponse } from "src/app/shared/models/PagedResponse";
import Swal from "sweetalert2";
import { ConfigDocumentosDetalle } from "./config-documentos-detalle";

@Component({
	selector: "app-cuadro-documento-detalle",
	templateUrl: "./cuadro-documento-detalle.component.html",
	styleUrls: ["./cuadro-documento-detalle.component.scss"],
})
export class CuadroDocumentoDetalleComponent implements OnInit, OnDestroy {
	@ViewChild("listaBuscaDocumento") private listaBuscaDocumento: TemplateRef<any>;
	public loading: boolean = false;
	public searchCatalog: DteEmitido[] = [];
	public documentoBuscado: DteEmitido = null;
	public errorBusqueda = false;
	public mostrarResultado = () => true;
	private busqueda$ = new Subject<string>();
	private destroy$ = new Subject<void>();
	public searchLoading: boolean = false;
	public detalleDocumentoFormGroup: FormGroup;
	public filter = new DteFilter();
	public consultandoDocumento: boolean = false;
	public documentRows: DteEmitido[] = [];
	public pageDocuments = new Page();
	public tipoDteInicialId: string;
	public errorDocumentos = false;
	private consultaDocumentos?: Subscription;
	private documentListModalRef: any;
	public configLocal = ConfigDocumentosDetalle;
	private currentEmisorId = this.tokenService.getCompany();
	private currentEmisor: Emisor = (JSON.parse(this.localStorage.getEmisores()) as Emisor[]).find((em) => em.emisorId == this.currentEmisorId);
	public tipoDteSource: TipoDte[];
	public selectedDocuments: DteEmitido[] = [];
	public currentDetalle: DetalleDte;
	constructor(private formBuilder: FormBuilder, private modalService: NgbModal, private dteService: DteEmitidosService, private facturador: FacturacionIndividualService, private utilities: UtilitiesService, private tokenService: TokenStorageService, private ref: ChangeDetectorRef, private localStorage: LocalStorageService) {}

	ngOnInit(): void {
		this.tipoDteSource = JSON.parse(this.localStorage.getDteTypes()) as TipoDte[];
		this.createDocumentForm();
		this.filter.emisorId = this.currentEmisorId;
		this.filter.sucursalId = this.tokenService.getSubsidiary();
		this.filter.ambienteId = this.currentEmisor.ambiente;
		this.pageDocuments.totalElements = 0;
		this.pageDocuments.totalPages = 0;
		this.reiniciarFiltrosDocumentos();
		this.facturador.selectedClientObservable.pipe(
			map(cliente => cliente?.receptorId),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe(() => {
			this.limpiarBusqueda();
			this.cancelarConsulta();
			this.reiniciarFiltrosDocumentos();
			this.selectedDocuments = [];
			this.documentRows = [];
			this.documentListModalRef?.dismiss("cambio-cliente");
		});
		this.busqueda$.pipe(
			switchMap(termino => {
				this.searchCatalog = [];
				this.errorBusqueda = false;
				this.searchLoading = !!termino.trim() && this.hayCliente;
				if (!this.searchLoading) return of([]);
				return timer(300).pipe(
					switchMap(() => this.buscarDocumentos(termino)),
					catchError(() => {
						this.errorBusqueda = true;
						return of([]);
					})
				);
			}),
			takeUntil(this.destroy$)
		).subscribe(documentos => {
			this.searchCatalog = documentos;
			this.searchLoading = false;
			this.ref.detectChanges();
		});
	}

	get hayCliente(): boolean {
		return !!this.facturador.selectedClient?.receptorId;
	}

	private reiniciarFiltrosDocumentos(): void {
		const tipos = this.tipoDteSource.filter(tipo => tipo.codigosii > 0 && tipo.codigosii < 110 && tipo.codigosii != 52 && tipo.codigosii != 46);
		const inicial = tipos.find(tipo => Number(tipo.codigosii) === 33) || tipos[0];
		this.tipoDteInicialId = inicial?.tipoDteId;
		this.filter = new DteFilter();
		this.filter.emisorId = this.currentEmisorId;
		this.filter.sucursalId = this.tokenService.getSubsidiary();
		this.filter.ambienteId = this.currentEmisor.ambiente;
		this.filter.receptorId = this.facturador.selectedClient?.receptorId;
		this.filter.tipoDteId = this.tipoDteInicialId ? [this.tipoDteInicialId] : [];
	}

	searchItem(event: { term: string }): void {
		// También contempla consumidores que asignan selectedClient sin emitir un evento.
		if (this.filter.receptorId !== this.facturador.selectedClient?.receptorId) this.reiniciarFiltrosDocumentos();
		this.busqueda$.next(event.term);
	}

	limpiarBusqueda(): void {
		this.documentoBuscado = null;
		this.searchCatalog = [];
		this.busqueda$.next("");
	}

	private buscarDocumentos(termino: string): Observable<DteEmitido[]> {
		if (!this.hayCliente) return of([]);
		const filtro = { ...this.filter };
		const texto = termino.trim();
		const normalizar = (valor: string) => valor.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
		if (/^\d+$/.test(texto)) {
			filtro.folio = texto;
		} else {
			const tipos = this.tipoDteSource.filter(tipo => normalizar(tipo.nombre).includes(normalizar(texto)));
			if (tipos.length) {
				const activos = Array.isArray(filtro.tipoDteId) ? filtro.tipoDteId : filtro.tipoDteId ? [filtro.tipoDteId] : [];
				const ids = tipos.map(tipo => tipo.tipoDteId).filter(id => !activos.length || activos.includes(id));
				if (!ids.length) return of([]);
				filtro.tipoDteId = ids;
			} else {
				filtro.razonSocialReceptor = texto;
			}
		}
		const page = Object.assign(new Page(), { pageNumber: 1, pageSize: 20 });
		return this.consultarDocumentos(filtro, page).pipe(map(respuesta => respuesta.data));
	}

	private consultarDocumentos(filtro: DteFilter, pagina: Page): Observable<PagedResponse<DteEmitido>> {
		// La búsqueda rápida y el modal comparten endpoint, ámbito y transformación.
		return this.dteService.documentsOfClient(filtro, pagina).pipe(map(respuesta => ({
			...respuesta,
			data: (respuesta.data || []).map(documento => {
				const row = { ...documento };
				this.setTipoDteToElement(row);
				row.nombreSucursal = this.setNombreSucursal(row.sucursalId);
				return row;
			}),
		})));
	}

	seleccionarResultado(documento: DteEmitido): void {
		if (!documento?.dteId || !this.hayCliente ||
			this.filter.receptorId !== this.facturador.selectedClient.receptorId ||
			!this.searchCatalog.some(row => row.dteId === documento.dteId)) {
			this.limpiarBusqueda();
			return;
		}
		this.currentDetalle = this.createDetalleDte([documento]);
		this.selectedDocumentToForm(this.currentDetalle);
		this.onSubmit();
		this.limpiarBusqueda();
	}

	createDetalleDteFromForm(): DetalleDte {
        let detalle = new DetalleDte();
        detalle.nombre = this.detalleDocumentoFormGroup.value.nombreItem;
        detalle.descripcion = this.detalleDocumentoFormGroup.value.descripcion;
        detalle.cantidad = this.detalleDocumentoFormGroup.value.cantidad;
        detalle.codigoInterno = this.detalleDocumentoFormGroup.value.codigo?.toString().trim();
        detalle.total = this.detalleDocumentoFormGroup.value.total;
        detalle.descuento = this.detalleDocumentoFormGroup.value.descuento;
        detalle.recargo = this.detalleDocumentoFormGroup.value.recargo;
        detalle.esExento = this.detalleDocumentoFormGroup.value.exento;
		detalle.precioUnitario = detalle.total
        return detalle;
    }
    agregarManual() {
        if (this.detalleDocumentoFormGroup.valid) {
            this.currentDetalle = this.createDetalleDteFromForm();
        }
        this.onSubmit();
    }


	openDocumentsListModal(modalRef: TemplateRef<any> = this.listaBuscaDocumento) {
		if (this.facturador.selectedClient == undefined) {
			Swal.fire({
				icon: "error",
				title: "Sin Cliente",
				text: "No ha seleccionado un cliente para la consulta de documentos. Intente nuevamente.",
			});
			return;
		}
		this.limpiarBusqueda();
		this.reiniciarFiltrosDocumentos();
		this.pageDocuments.pageNumber = 0;
		this.pageDocuments.pageSize = 10;
		this.pageDocuments.totalElements = 0;
		this.documentRows = [];
		this.selectedDocuments = [];
		this.documentListModalRef = this.modalService.open(modalRef, {
			windowClass: "liquidacion-documentos-window",
			size: "xl",
			centered: true,
			scrollable: true,
		});
		this.documentListModalRef.result.then(() => this.cancelarConsulta(), () => this.cancelarConsulta());
		this.setPageDocuments({ offset: 0 });
	}

	onSubmit() {
		if (this.detalleDocumentoFormGroup.valid) {
			this.facturador.publishDocumentDetail(this.currentDetalle);
			this.detalleDocumentoFormGroup.reset();
			this.currentDetalle = new DetalleDte();
		}
	}

	setPageDocuments(pageInfo: { offset: number }): void {
		this.consultaDocumentos?.unsubscribe();
		this.consultandoDocumento = true;
		this.errorDocumentos = false;
		const localPage = { ...this.pageDocuments, pageNumber: pageInfo.offset + 1 };
		this.consultaDocumentos = this.consultarDocumentos({ ...this.filter }, localPage).subscribe(
			result => {
				this.documentRows = result.data;
				this.setPagefromResponse(this.pageDocuments, result);
				this.consultandoDocumento = false;
				this.ref.detectChanges();
			},
			() => {
				this.documentRows = [];
				this.pageDocuments.totalElements = 0;
				this.pageDocuments.pageNumber = 0;
				this.consultandoDocumento = false;
				this.errorDocumentos = true;
				this.ref.detectChanges();
			}
		);
	}

	cambiarTamanoPagina(tamano: number): void {
		this.pageDocuments.pageSize = Number(tamano);
		this.setPageDocuments({ offset: 0 });
	}

	get primerDocumento(): number {
		return this.documentRows.length ? this.pageDocuments.pageNumber * this.pageDocuments.pageSize + 1 : 0;
	}

	get ultimoDocumento(): number {
		return this.documentRows.length ? this.primerDocumento + this.documentRows.length - 1 : 0;
	}

	get todosSeleccionadosEnPagina(): boolean {
		return this.documentRows.length > 0 && this.documentRows.every(row => this.estaSeleccionado(row));
	}

	get seleccionParcialEnPagina(): boolean {
		return !this.todosSeleccionadosEnPagina && this.documentRows.some(row => this.estaSeleccionado(row));
	}

	estaSeleccionado(documento: DteEmitido): boolean {
		return this.selectedDocuments.some(row => row.dteId === documento.dteId);
	}

	seleccionarDocumento(documento: DteEmitido, seleccionado: boolean): void {
		this.selectedDocuments = this.selectedDocuments.filter(row => row.dteId !== documento.dteId);
		if (seleccionado) this.selectedDocuments = [...this.selectedDocuments, documento];
	}

	get totalSeleccionado(): number {
		return this.selectedDocuments.reduce((total, row) => total + Number(row.total || 0), 0);
	}

	private cancelarConsulta(): void {
		this.consultaDocumentos?.unsubscribe();
		this.consultandoDocumento = false;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.busqueda$.complete();
		this.cancelarConsulta();
	}

	createDetalleDte(dteArray: DteEmitido[]): DetalleDte {
		let newDetail: DetalleDte = new DetalleDte();
		newDetail.relatedDocumentsIds = dteArray.map((d) => d.dteId);
		newDetail.codigoInterno = dteArray[0].codigoTipoDte.toString();
		newDetail.cantidad = 1;
		newDetail.total = dteArray.reduce((sum, current) => (sum += current.total), 0);
		newDetail.descripcion = dteArray.reduce((folioStrings, current) => (folioStrings += current.folio + ", "), "");
		newDetail.descripcion = "Folio: " + newDetail.descripcion.slice(0, newDetail.descripcion.length - 2);
		newDetail.nombre = dteArray[0].nombreTipoDte;
		newDetail.precioUnitario = newDetail.total;
		newDetail.esExento = newDetail.codigoInterno == "34" || newDetail.codigoInterno == "41";
		return newDetail;
	}

	selectedDocumentToForm(detalle: DetalleDte) {
		this.detalleDocumentoFormGroup.controls.nombreItem.patchValue(detalle.nombre);
		this.detalleDocumentoFormGroup.controls.descripcion.patchValue(detalle.descripcion), this.detalleDocumentoFormGroup.controls.cantidad.patchValue(detalle.cantidad);
		this.detalleDocumentoFormGroup.controls.codigo.patchValue(detalle.codigoInterno);
		this.detalleDocumentoFormGroup.controls.total.patchValue(detalle.total);
		this.detalleDocumentoFormGroup.controls.descuento.patchValue(detalle.descuento);
		this.detalleDocumentoFormGroup.controls.recargo.patchValue(detalle.recargo);
		this.detalleDocumentoFormGroup.controls.exento.patchValue(detalle.esExento);

	}

	searchElementsByFilter(event) {
		this.limpiarBusqueda();
		this.selectedDocuments = [];
		this.filter = this.filterConcordance(this.configLocal.barraBusqueda.criteriosKeys, this.filter, event);
		this.filter.tipoDteId = event.tipoDteId != null ? event.tipoDteId : "";
		this.filter.fechaDesde = event.fechaInicio;
		this.filter.fechaHasta = event.fechaFin;
		this.filter.importado = event.importado ? true : false;
		this.filter[event.criterio] = event.busqueda;
		this.filter.estadoSii = event.estadoDte;
		this.setPageDocuments({ offset: 0 });
	}

	filterConcordance(configCriterias, dteFilter: DteFilter, eventFilter): DteFilter {
		//vaciar del dteFilter aquellos campos distinto al presnetado en el eventFilter actual, segun el arreglo configCriterias
		configCriterias.forEach((criteria) => {
			if (criteria != eventFilter.criterio) {
				dteFilter[criteria] = "";
			}
		});
		return dteFilter;
	}

	createDocumentForm() {
		this.detalleDocumentoFormGroup = this.formBuilder.group({
			codigo: [, [Validators.required, Validators.maxLength(3), Validators.pattern("^[0-9]{1,3}$")]],
			nombreItem: [, Validators.required],
			cantidad: [, Validators.required],
			total: [, Validators.required],
			descripcion: [],
			descuento: [],
			recargo: [],
			exento: [false],
		});
	}

	setTipoDteToElement(row: IDte) {
		if (row.tipoDteId) {
			let tipoDteAsociado = this.tipoDteSource.find((tipo) => tipo.tipoDteId == row.tipoDteId);
			row.nombreTipoDte = tipoDteAsociado?.nombre || row.nombreTipoDte || "Documento";
			if (tipoDteAsociado) row.codigoTipoDte = tipoDteAsociado.codigosii;
		}
	}

	setNombreSucursal(sucursalId: string): string {
		let lista = JSON.parse(this.localStorage.getSucursales()) as Sucursal[];
		let sucursalEncontrada = lista.find((suc) => suc.sucursalId === sucursalId);
		if (!sucursalEncontrada) {
		  return "Sucursal no encontrada";
		}
	  
		return sucursalEncontrada.nombre;
	  }
	  

	setPagefromResponse(currentPage: Page, data: PagedResponse<DteEmitido>) {
		currentPage.pageSize = data.pageSize;
		currentPage.pageNumber = data.pageNumber - 1 < 0 ? 0 : data.pageNumber - 1;
		currentPage.totalPages = data.totalPages;
		currentPage.totalElements = data.totalRecords;
	}
	selectAll(seleccionado: boolean): void {
		const idsPagina = new Set(this.documentRows.map(row => row.dteId));
		this.selectedDocuments = this.selectedDocuments.filter(row => !idsPagina.has(row.dteId));
		if (seleccionado) this.selectedDocuments = [...this.selectedDocuments, ...this.documentRows];
	}

	procesarDocumentsADetalles() {
		//check for same dte type
		if (this.consultandoDocumento || this.selectedDocuments.length == 0) return;
		if (!this.selectedDocuments.every((dte) => dte.tipoDteId == this.selectedDocuments[0].tipoDteId)) {
			Swal.fire({
				icon: "error",
				title: "Documentos de distinto tipo",
				text: "Ha seleccionado documentos de distintos tipos para generar un elemento de detalle en la Liquidación. Seleccione solo documentos de un tipo por cada línea de detalle.",
			});
			return;
		}
		//transform to DetalleDte
		let detalle = this.createDetalleDte(this.selectedDocuments);
		this.currentDetalle = detalle;
		this.selectedDocumentToForm(detalle);
		this.onSubmit();
		this.documentListModalRef.close();
	}
}
